import { modules } from '../modules/registry';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Key, FileText, Globe, Shield, Database, FolderKey, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../utils/api';
import { decryptField } from '../stores/vaultStore';
import { useAuthStore } from '../stores/authStore';

interface SearchResult {
  id: string;
  vaultId: string;
  vaultName: string;
  name: string;
  type: string;
  username?: string;
  criticality: string;
  environment: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_ICONS: Record<string, typeof Key> = {
  LOGIN: Key,
  API_KEY: Key,
  SSH_KEY: Key,
  CERTIFICATE: Shield,
  NOTE: FileText,
  DATABASE: Database,
  CUSTOM: Globe,
};

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [allItems, setAllItems] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);

  // ── Fetch all items (with cache) ──────────────────────────────────────
  const loadAllItems = useCallback(async (_force = false) => {
    const identity = useAuthStore.getState();
    if (!identity.masterKey) { setDataLoaded(true); return; }
    setLoading(true);
    try {
      const { data: vaultsData } = await api.get('/vaults');
      const vaults: Array<{ id: string; nameEnc?: unknown; name?: string }> =
        Array.isArray(vaultsData) ? vaultsData : (vaultsData.vaults ?? []);

      const allResults: SearchResult[] = [];

      // Fetch items from each vault in parallel
      const itemPromises = vaults.map(async (vault) => {
        try {
          const { data } = await api.get(`/vaults/${vault.id}/items`, { params: { limit: 500 } });
          const items = Array.isArray(data) ? data : (data.items ?? []);
          const masterKey = useAuthStore.getState().masterKey;
          const vaultName = masterKey && vault.nameEnc
            ? await decryptField(vault.nameEnc, masterKey) || vault.name || 'Project'
            : vault.name || 'Project';

          return await Promise.all(items.map(async (item: Record<string, unknown>) => ({
            id: item.id as string,
            vaultId: vault.id,
            vaultName,
            name: masterKey ? (await decryptField(item.nameEnc, masterKey)) || '[encrypted]' : '[encrypted]',
            type: ((item.type as string) || 'LOGIN').toUpperCase(),
            username: masterKey ? (await decryptField(item.usernameEnc, masterKey)) || undefined : undefined,
            criticality: ((item.criticality as string) || 'MEDIUM').toUpperCase(),
            environment: ((item.environment as string) || 'ANY').toUpperCase(),
          })));
        } catch {
          return [];
        }
      });

      const vaultItems = await Promise.all(itemPromises);
      vaultItems.forEach((items) => allResults.push(...items));

      // Discard results if the session locked or changed while requests were pending.
      if (useAuthStore.getState().masterKey !== identity.masterKey || useAuthStore.getState().user?.id !== identity.user?.id) return;
      setAllItems(allResults);
      setResults(allResults);
      setDataLoaded(true);
    } catch {
      // Silently fail — search just won't have data
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Filter items as user types ────────────────────────────────────────
  useEffect(() => {
    if (!query.trim()) {
      setResults(allItems);
      setSelectedIndex(0);
      return;
    }

    const q = query.toLowerCase();
    const filtered = allItems.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        (item.username && item.username.toLowerCase().includes(q)) ||
        item.type.toLowerCase().includes(q) ||
        item.vaultName.toLowerCase().includes(q),
    );
    setResults(filtered);
    setSelectedIndex(0);
  }, [query, allItems]);

  // ── Open effects — only reset query, keep cached data ─────────────────
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input after animation
      setTimeout(() => inputRef.current?.focus(), 50);
      // Load only for this mounted, unlocked session.
      loadAllItems();
    }
  }, [isOpen, loadAllItems]);

  // ── Keyboard navigation ───────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        navigateToItem(results[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);



  const navigateToItem = (item: SearchResult) => {
    onClose();
    navigate(`/items/${item.id}`);
  };

  const handleRefresh = () => {
    setDataLoaded(false);
    loadAllItems(true);
  };

  const getIcon = (type: string) => {
    const Icon = TYPE_ICONS[type] || Key;
    return Icon;
  };

  const getCriticalityColor = (crit: string) => {
    switch (crit) {
      case 'CRITICAL': return 'text-red-400';
      case 'HIGH': return 'text-orange-400';
      case 'MEDIUM': return 'text-yellow-400';
      case 'LOW': return 'text-green-400';
      default: return 'text-zinc-400';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-zinc-700 px-4 py-3">
          <Search className="h-5 w-5 text-zinc-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search secrets, projects, types…"
            className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-500 outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
          </button>
          <kbd className="hidden rounded border border-zinc-600 bg-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-400 sm:inline">
            ESC
          </kbd>
        </div>

        <nav aria-label="Resultados de módulos" className="max-h-40 overflow-auto border-b border-zinc-700 p-2">
          {modules.filter(module => module.navigation && module.name.toLowerCase().includes(query.toLowerCase())).map(module => <button key={module.id} className="block w-full rounded px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800" onClick={() => { navigate(module.route); onClose(); }}>{module.name}</button>)}
        </nav>
        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {loading && allItems.length === 0 && (
            <div className="flex items-center justify-center py-8 text-sm text-zinc-500">
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-zinc-500 border-t-transparent" />
              Loading secrets…
            </div>
          )}

          {!loading && dataLoaded && results.length === 0 && (
            <div className="py-8 text-center text-sm text-zinc-500">
              {query ? `No results for "${query}"` : 'No secrets found'}
            </div>
          )}

          {results.map((item, index) => {
            const Icon = getIcon(item.type);
            const isSelected = index === selectedIndex;

            return (
              <button
                key={item.id}
                onClick={() => navigateToItem(item)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={clsx(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                  isSelected
                    ? 'bg-green-500/10 text-zinc-100'
                    : 'text-zinc-300 hover:bg-zinc-800',
                )}
              >
                <div className={clsx(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                  isSelected ? 'bg-green-500/20' : 'bg-zinc-800',
                )}>
                  <Icon className={clsx('h-4 w-4', isSelected ? 'text-green-400' : 'text-zinc-400')} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{item.name}</span>
                    <span className={clsx('text-[10px] font-medium', getCriticalityColor(item.criticality))}>
                      {item.criticality}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
                    {item.username && (
                      <span className="truncate">{item.username}</span>
                    )}
                    <span className="shrink-0">{item.type.replace('_', ' ')}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <FolderKey className="h-3 w-3" />
                  <span className="truncate max-w-[100px]">{item.vaultName}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-zinc-700 px-4 py-2 text-[11px] text-zinc-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-zinc-600 bg-zinc-700 px-1 py-0.5">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-zinc-600 bg-zinc-700 px-1 py-0.5">↵</kbd>
              open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-zinc-600 bg-zinc-700 px-1 py-0.5">esc</kbd>
              close
            </span>
          </div>
          <span>{results.length} results</span>
        </div>
      </div>
    </div>
  );
}
